import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import {
  Rocket, RefreshCw, Loader2, AlertTriangle, CheckCircle2, Power,
  IndianRupee, Mail, MessageSquare, Crown, Copy, Eye, EyeOff, Trophy,
  Route, Coins, X, Target, Send, Users, ShieldCheck,
} from 'lucide-react';

// Daruwalla BDE — the lead → mandate engine console.
// Pipeline truth, the escalation inbox, and the kill switch. The hourly agent
// and the backend do the work; this page is where Ruhshad watches money form.

const STAGE_LABELS = {
  sourced: 'Sourced', enriched: 'Contact found', queued: 'Queued',
  contacted: 'Contacted', warming: 'Warming', replied: 'Replied',
  escalated: 'Needs Ruhshad', meeting: 'Meeting', won: 'Won',
  lost: 'Lost', suppressed: 'Suppressed',
};

const STAGE_COLORS = {
  sourced: 'bg-slate-100 text-slate-600', enriched: 'bg-sky-100 text-sky-700',
  queued: 'bg-indigo-100 text-indigo-700', contacted: 'bg-blue-100 text-blue-700',
  warming: 'bg-amber-100 text-amber-700', replied: 'bg-emerald-100 text-emerald-700',
  escalated: 'bg-red-100 text-red-700', meeting: 'bg-purple-100 text-purple-700',
  won: 'bg-green-100 text-green-800', lost: 'bg-slate-100 text-slate-400',
  suppressed: 'bg-slate-100 text-slate-400',
};

// The house style Ruhshad approved on 7 Sep 2026. Used only by the approval
// section below; the rest of this page keeps its existing Tailwind.
const DCS = {
  cream: '#F7F1E3', paper: '#FFFDF8', ink: '#2A241A',
  gold: '#AD8A2E', hairline: '#E6DAC0',
};

// One page of the approval queue. Gating every source parks ~121 Apollo leads
// here; 121 full emails on one screen is not a screen anyone can use.
const APPR_PAGE = 20;

const IST_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const hhmm = (h) => {
  if (h == null) return '—';
  const hr = Math.floor(h);
  const mn = Math.round((h - hr) * 60);
  return `${String(hr).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
};

// Every safety limit is an editable number he can see, not a constant hidden
// in the backend. Saves on blur, so a half-typed number is never sent.
const DripNum = ({ label, value, step = 1, suffix, onSave, disabled }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <label className="text-xs flex items-center gap-1.5" style={{ color: DCS.ink }}>
      <span style={{ opacity: 0.7 }}>{label}</span>
      <input
        type="number" step={step} value={v == null ? '' : v} disabled={disabled}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (String(v) !== String(value) && v !== '') onSave(Number(v)); }}
        className="w-20 rounded px-1.5 py-0.5 text-right disabled:opacity-50"
        style={{ background: DCS.paper, border: `1px solid ${DCS.hairline}`, color: DCS.ink }}
      />
      {suffix && <span style={{ opacity: 0.5 }}>{suffix}</span>}
    </label>
  );
};

const inr = (v) => {
  if (!v) return '—';
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)} L`;
  return `₹${Number(v).toLocaleString('en-IN')}`;
};

const Stat = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</p>
  </div>
);

export default function BDEPage() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [escalated, setEscalated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [showKey, setShowKey] = useState(false);
  // "Waiting for your approval" — the drafted mails, and his yes/no on each.
  const [approvals, setApprovals] = useState(null);
  const [apprCollapsed, setApprCollapsed] = useState(false);
  const [apprTick, setApprTick] = useState(0);      // bump to refetch
  const [apprOffset, setApprOffset] = useState(0);
  const [apprSource, setApprSource] = useState('');
  const [apprSel, setApprSel] = useState({});       // lead_id -> checked
  const [apprOpen, setApprOpen] = useState({});     // lead_id -> expanded?
  const [apprFull, setApprFull] = useState({});     // lead_id -> full row (on demand)
  const [apprEdit, setApprEdit] = useState(null);   // { leadId, subject, body }
  const [apprBusy, setApprBusy] = useState('');
  const [dripBusy, setDripBusy] = useState(false);
  const [sources, setSources] = useState(null);   // cost & return per channel
  const [ledger, setLedger] = useState(null);     // per-lead provenance drawer
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [engage, setEngage] = useState(null);    // screen → score → send drawer
  const [engageBusy, setEngageBusy] = useState(false);
  const [manualFamily, setManualFamily] = useState(''); // hand-picked discipline when the title can't be read

  const load = useCallback(async () => {
    try {
      const [p, s, esc] = await Promise.all([
        api.get('/bde/pipeline'),
        api.get('/bde/settings'),
        api.get('/bde/leads?stage=escalated&limit=20'),
      ]);
      setData(p.data);
      setSettings(s.data);
      setEscalated(esc.data.leads || []);
      setError('');
      setApprTick((t) => t + 1);   // refresh the approval queue on the same beat
      try {
        const src = await api.get('/bde/analytics/sources');
        setSources(src.data);
      } catch (_) {
        setSources(null);   // ledger not deployed yet - hide the section
      }
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // The approval queue fetches on its own, keyed on the page and filter he is
  // looking at, so paging never waits for the whole dashboard. A failure just
  // hides the section — the page still works on a box without the module.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ limit: String(APPR_PAGE), offset: String(apprOffset) });
        if (apprSource) qs.set('source', apprSource);
        const r = await api.get(`/bde/approvals/queue?${qs.toString()}`);
        if (!cancelled) setApprovals(r.data);
      } catch (_) {
        if (!cancelled) setApprovals(null);
      }
    })();
    return () => { cancelled = true; };
  }, [apprOffset, apprSource, apprTick]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/bde/approvals/ui-state');
        setApprCollapsed(!!r.data.collapsed);
      } catch (_) { /* module not installed yet */ }
    })();
  }, []);

  const openLedger = async (id) => {
    setLedgerBusy(true);
    setLedger({ loading: true });
    try {
      const r = await api.get(`/bde/leads/${id}/ledger`);
      setLedger(r.data);
    } catch (e) {
      setLedger({ error: e.response?.data?.detail || String(e.message || e) });
    }
    setLedgerBusy(false);
  };

  // Two steps on purpose. The first click only looks: it screens, scores and
  // drafts, and creates nothing. Sending is a separate, deliberate click once
  // Ruhshad has seen who it picked and why.
  const previewEngage = async (id, familyOverride) => {
    setEngageBusy(true);
    setEngage({ loading: true, leadId: id });
    try {
      const body = { dry_run: true, max_profiles: 3 };
      if (familyOverride) body.family_override = familyOverride;
      const r = await api.post(`/bde/leads/${id}/engage`, body);
      setEngage({ ...r.data, leadId: id });
    } catch (e) {
      setEngage({ error: e.response?.data?.detail || String(e.message || e), leadId: id });
    }
    setEngageBusy(false);
  };

  const confirmEngage = async (id, familyOverride) => {
    setEngageBusy(true);
    setEngage((prev) => ({ ...prev, sending: true }));
    try {
      const body = { dry_run: false, max_profiles: 3 };
      if (familyOverride) body.family_override = familyOverride;
      const r = await api.post(`/bde/leads/${id}/engage`, body);
      setEngage({ ...r.data, leadId: id, done: true });
      load();
    } catch (e) {
      setEngage((prev) => ({
        ...prev, sending: false,
        error: e.response?.data?.detail || String(e.message || e),
      }));
    }
    setEngageBusy(false);
  };

  const reloadApprovals = () => setApprTick((t) => t + 1);

  const apprError = (e) => setError(e.response?.data?.detail || String(e.message || e));

  const setCollapsed = async (next) => {
    setApprCollapsed(next);
    try { await api.put('/bde/approvals/ui-state', { collapsed: next }); } catch (_) { /* local only */ }
  };

  // Expanding fetches the body for that one lead. The list itself never
  // carries 121 email bodies.
  const expandRow = async (row) => {
    const open = !!apprOpen[row.lead_id];
    setApprOpen((p) => ({ ...p, [row.lead_id]: !open }));
    if (!open && !apprFull[row.lead_id]) {
      try {
        const r = await api.get(`/bde/approvals/${row.lead_id}`);
        setApprFull((p) => ({ ...p, [row.lead_id]: r.data }));
      } catch (e) { apprError(e); }
    }
  };

  const rowCopy = (row) => apprFull[row.lead_id]?.email || row.email || {};

  // Select-all ticks what is on screen under the current filter — never
  // silently everything — and never a row that cannot be approved.
  const selectAllVisible = (visible, on) => {
    const next = { ...apprSel };
    visible.forEach((r) => { if (r.can_approve) next[r.lead_id] = on; });
    setApprSel(next);
  };

  const approveMail = async (row, override) => {
    const copy = override || rowCopy(row);
    if (!window.confirm(
      `Approve this email to ${row.company}?\n\nSubject: ${copy.subject || ''}\n\n`
      + 'This click sends nothing. It tells the engine these exact words are allowed '
      + 'to go out, under the caps, the rolling ceiling and the send window.')) return;
    setApprBusy(row.lead_id);
    try {
      await api.post(`/bde/approvals/${row.lead_id}/approve`,
                     { seen_fingerprint: copy.fingerprint });
      setApprEdit(null);
      setApprFull((p) => { const n = { ...p }; delete n[row.lead_id]; return n; });
      reloadApprovals();
    } catch (e) { apprError(e); }
    setApprBusy('');
  };

  const approveSelected = async (visible) => {
    const chosen = visible.filter((r) => apprSel[r.lead_id]);
    const ok = chosen.filter((r) => r.can_approve);
    const no = chosen.filter((r) => !r.can_approve);
    if (!chosen.length) return;
    let msg = `Approve ${ok.length} email${ok.length === 1 ? '' : 's'}?\n\n`
      + ok.map((r) => `• ${r.company} — ${r.role || 'role not stated'}`).join('\n');
    if (no.length) {
      msg += `\n\n${no.length} of ${chosen.length} selected cannot be approved yet — `
        + 'they will be left alone.';
    }
    msg += '\n\nNothing is sent by this click. Each one records its own copy of the exact '
      + 'email you approved.';
    if (!ok.length || !window.confirm(msg)) return;
    setApprBusy('bulk');
    try {
      const fps = {};
      ok.forEach((r) => { const f = rowCopy(r).fingerprint; if (f) fps[r.lead_id] = f; });
      const r = await api.post('/bde/approvals/approve-bulk',
                               { lead_ids: ok.map((x) => x.lead_id), seen_fingerprints: fps });
      setApprSel({});
      setApprFull({});
      if (r.data.skipped_count) {
        setError(`${r.data.approved_count} approved, ${r.data.skipped_count} left alone: `
          + r.data.skipped.map((s) => `${s.company || s.lead_id} — ${s.reason}`).join('; '));
      }
      reloadApprovals();
    } catch (e) { apprError(e); }
    setApprBusy('');
  };

  const saveAndApprove = async (row) => {
    if (!apprEdit) return;
    setApprBusy(row.lead_id);
    let saved = null;
    try {
      const r = await api.put(`/bde/approvals/${row.lead_id}/copy`,
                              { subject: apprEdit.subject, body: apprEdit.body });
      saved = r.data;
    } catch (e) { apprError(e); }
    setApprBusy('');
    if (saved) await approveMail(row, { subject: saved.subject, fingerprint: saved.fingerprint });
  };

  const rejectMail = async (row) => {
    if (!window.confirm(
      `Reject the email to ${row.company}?\n\nIt leaves this list and nothing will be `
      + 'sent to them. The lead itself is kept — you can come back to it.')) return;
    const reason = window.prompt(`Why is ${row.company} being rejected? (optional)`) || '';
    setApprBusy(row.lead_id);
    try {
      await api.post(`/bde/approvals/${row.lead_id}/reject`, { reason });
      reloadApprovals();
    } catch (e) { apprError(e); }
    setApprBusy('');
  };

  const saveDrip = async (patch, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setDripBusy(true);
    try {
      const r = await api.put('/bde/approvals/drip/settings', patch);
      setApprovals((prev) => (prev ? { ...prev, drip: r.data } : prev));
    } catch (e) { apprError(e); }
    setDripBusy(false);
  };

  const stopDrip = async () => {
    if (!window.confirm(
      'STOP auto-approve now?\n\nEverything still waiting stays unapproved, which means '
      + 'none of it can be sent by anything.')) return;
    setDripBusy(true);
    try {
      const r = await api.post('/bde/approvals/drip/stop');
      setApprovals((prev) => (prev ? { ...prev, drip: r.data } : prev));
      reloadApprovals();
    } catch (e) { apprError(e); }
    setDripBusy(false);
  };

  const toggle = async (field) => {
    setBusy(field);
    try {
      const res = await api.post('/bde/settings', { [field]: !settings[field] });
      setSettings((prev) => ({ ...prev, ...res.data }));
    } catch (e) {
      setError(String(e.message || e));
    }
    setBusy('');
  };

  const runNow = async () => {
    setBusy('run');
    try {
      await api.post('/bde/run-cycle');
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  const convert = async (leadId) => {
    setBusy(`convert-${leadId}`);
    try {
      await api.post(`/bde/leads/${leadId}/convert`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const stages = data?.stages || {};
  const active = ['enriched', 'queued', 'contacted', 'warming', 'replied', 'escalated', 'meeting']
    .reduce((a, s) => a + (stages[s] || 0), 0);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5 max-md:pb-[calc(1.5rem+env(safe-area-inset-bottom))]" data-testid="bde-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Rocket className="w-5 h-5 text-purple-600" />
            Daruwalla BDE
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            The lead → mandate engine. Sources hiring companies, finds the decision maker,
            warms them automatically, and hands you the conversation the moment money enters it.
          </p>
        </div>
        <div className="flex items-center gap-2 max-md:w-full max-md:grid max-md:grid-cols-2 [&>button]:max-md:min-h-[44px] [&>button]:max-md:justify-center">
          <button
            onClick={runNow}
            disabled={!!busy}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40"
            data-testid="bde-run-now"
          >
            {busy === 'run' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Run cycle now
          </button>
          <button
            onClick={() => toggle('engine_enabled')}
            disabled={!!busy}
            className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg text-white disabled:opacity-40 ${
              settings?.engine_enabled ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-red-600 hover:bg-red-500'
            }`}
            data-testid="bde-kill-switch"
          >
            <Power className="w-3.5 h-3.5" />
            {settings?.engine_enabled ? 'Engine ON' : 'Engine OFF'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 text-xs text-red-600 bg-red-50 rounded-lg">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="break-all">{error}</span>
        </div>
      )}

      {/* Money row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Pipeline value" value={inr(data?.pipeline_value)} accent="text-purple-700" />
        <Stat label="Active leads" value={active} />
        <Stat label="Sent today" value={data?.today?.sent_today ?? 0} />
        <Stat label="Replies today" value={data?.today?.replies_today ?? 0} accent="text-emerald-600" />
        <Stat label="Mandates won" value={stages.won || 0} accent="text-green-700" />
      </div>

      {/* Stage strip */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex flex-wrap gap-2">
          {Object.keys(STAGE_LABELS).map((s) => (
            <span key={s} className={`text-xs px-2.5 py-1 rounded-full ${STAGE_COLORS[s]}`}>
              {STAGE_LABELS[s]}: <b>{stages[s] || 0}</b>
            </span>
          ))}
        </div>
      </div>

      {/* Needs Ruhshad */}
      {escalated.length > 0 && (
        <div className="bg-white border-2 border-red-200 rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-semibold text-red-700 flex items-center gap-2">
            <Crown className="w-4 h-4" /> Needs you — money is talking ({escalated.length})
          </h2>
          <div className="space-y-2">
            {escalated.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 max-md:flex-col max-md:items-stretch">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {l.company?.name} — {l.signal?.job_title}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {l.contact?.name} · {l.contact?.title} · {l.contact?.email}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 max-md:justify-between">
                  <span className="text-xs font-semibold text-slate-700">{inr(l.signal?.estimated_fee)}</span>
                  <button
                    onClick={() => convert(l.id)}
                    disabled={!!busy}
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-500 disabled:opacity-40 max-md:min-h-[44px] max-md:px-4"
                  >
                    {busy === `convert-${l.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trophy className="w-3 h-3" />}
                    Won → Mandate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waiting for your approval — the drafted mails, in the words that will
          actually go out. Nothing on this page can be emailed until you say
          yes, whatever source it came from. */}
      {approvals && (() => {
        const visible = approvals.queue || [];
        const drip = approvals.drip || {};
        const ds = drip.settings || {};
        const dl = drip.live || {};
        const selected = visible.filter((r) => apprSel[r.lead_id]);
        const selectable = visible.filter((r) => r.can_approve);
        const allTicked = selectable.length > 0
          && selectable.every((r) => apprSel[r.lead_id]);
        const page = approvals.page || {};
        return (
          <div className="rounded-xl" data-testid="bde-approvals"
               style={{ background: DCS.paper, border: `1px solid ${DCS.hairline}` }}>
            {/* header — always visible, collapses the rest */}
            <div className="p-5 pb-3 flex items-start justify-between flex-wrap gap-2">
              <button onClick={() => setCollapsed(!apprCollapsed)}
                      className="flex items-center gap-2 text-sm font-semibold"
                      style={{ color: DCS.ink }}>
                <span style={{ color: DCS.gold }}>{apprCollapsed ? '▸' : '▾'}</span>
                <ShieldCheck className="w-4 h-4" style={{ color: DCS.gold }} />
                Waiting for your approval
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: DCS.cream, color: DCS.gold, border: `1px solid ${DCS.hairline}` }}>
                  {approvals.count}
                </span>
              </button>
              <p className="text-xs max-w-md" style={{ color: DCS.ink, opacity: 0.6 }}>
                No lead of any source — Co-BDE, Apollo, imports — can be emailed until you
                approve it, and an approval is bound to the exact words you read.
              </p>
            </div>

            {!apprCollapsed && (
              <div className="px-5 pb-5 space-y-3">
                {/* toolbar: select-all (visible only), filter, bulk approve */}
                <div className="flex items-center gap-3 flex-wrap text-xs"
                     style={{ color: DCS.ink }}>
                  <label className="flex items-center gap-1.5">
                    <input type="checkbox" checked={allTicked}
                           disabled={selectable.length === 0}
                           onChange={(e) => selectAllVisible(visible, e.target.checked)} />
                    Select all shown ({selectable.length})
                  </label>
                  <span style={{ opacity: 0.6 }}>{selected.length} selected</span>
                  <button
                    onClick={() => approveSelected(visible)}
                    disabled={!!apprBusy || selected.length === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white disabled:opacity-40"
                    style={{ background: DCS.gold }}
                  >
                    {apprBusy === 'bulk'
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <CheckCircle2 className="w-3 h-3" />}
                    Approve selected
                  </button>
                  <span className="flex-1" />
                  <label className="flex items-center gap-1.5">
                    Source
                    <select value={apprSource}
                            onChange={(e) => { setApprSource(e.target.value); setApprOffset(0); setApprSel({}); }}
                            className="rounded px-1.5 py-0.5"
                            style={{ background: DCS.paper, border: `1px solid ${DCS.hairline}`, color: DCS.ink }}>
                      <option value="">All</option>
                      {(approvals.sources || []).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </label>
                </div>

                {/* rows */}
                <div className="space-y-2">
                  {visible.map((row) => {
                    const open = !!apprOpen[row.lead_id];
                    const full = apprFull[row.lead_id];
                    const copy = rowCopy(row);
                    const editing = apprEdit && apprEdit.leadId === row.lead_id;
                    const blockers = (full || row).blockers || [];
                    return (
                      <div key={row.lead_id} className="rounded-lg overflow-hidden"
                           style={{ background: DCS.cream, border: `1px solid ${DCS.hairline}` }}>
                        <div className="px-3 py-2.5 flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={!!apprSel[row.lead_id]}
                            disabled={!row.can_approve}
                            title={row.can_approve ? '' : row.blocked_reason || ''}
                            onChange={() => setApprSel((p) => ({ ...p, [row.lead_id]: !p[row.lead_id] }))}
                          />
                          <button onClick={() => expandRow(row)}
                                  className="flex-1 min-w-0 text-left">
                            <p className="text-sm font-medium truncate" style={{ color: DCS.ink }}>
                              {row.company} — {row.role || 'Role not stated'}
                            </p>
                            <p className="text-xs truncate" style={{ color: DCS.ink, opacity: 0.6 }}>
                              {row.location || 'Location unknown'} ·{' '}
                              {row.decision_maker?.email || 'no contact email yet'} ·{' '}
                              {row.source || 'unknown source'}
                            </p>
                            {!row.can_approve && (
                              <p className="text-xs mt-0.5 text-amber-700">{row.blocked_reason}</p>
                            )}
                          </button>
                          <span className="text-xs font-semibold flex-shrink-0" style={{ color: DCS.ink }}>
                            {inr(row.estimated_fee)}
                          </span>
                          <button onClick={() => expandRow(row)}
                                  className="text-xs whitespace-nowrap flex-shrink-0"
                                  style={{ color: DCS.gold }}>
                            {open ? 'Hide' : 'Read the email'}
                          </button>
                        </div>

                        {open && (
                          <div className="px-3 pb-3 space-y-3">
                            {!full && (
                              <p className="text-xs flex items-center gap-1.5" style={{ color: DCS.ink, opacity: 0.6 }}>
                                <Loader2 className="w-3 h-3 animate-spin" /> Loading the email…
                              </p>
                            )}
                            <div className="text-xs rounded-lg px-2.5 py-2"
                                 style={{ background: DCS.paper, border: `1px solid ${DCS.hairline}`, color: DCS.ink }}>
                              Read as <b>{row.role_read?.discipline || 'unclear'}</b>
                              {row.role_read?.confidence != null && ` (confidence ${row.role_read.confidence})`}
                              {row.role_read?.seniority ? `, ${row.role_read.seniority} level` : ''}
                              {full?.matched_candidates != null
                                && ` · ${full.matched_candidates} of our people match this brief`}
                            </div>

                            {blockers.length > 0 && (
                              <ul className="space-y-1">
                                {blockers.map((b) => (
                                  <li key={b.code} className="flex items-start gap-1.5 text-xs text-amber-800">
                                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                    <span>{b.message}</span>
                                  </li>
                                ))}
                              </ul>
                            )}

                            {editing ? (
                              <div className="space-y-2">
                                <input
                                  value={apprEdit.subject}
                                  onChange={(e) => setApprEdit({ ...apprEdit, subject: e.target.value })}
                                  placeholder="Subject line"
                                  className="w-full text-sm rounded px-2.5 py-1.5"
                                  style={{ background: DCS.paper, border: `1px solid ${DCS.hairline}`, color: DCS.ink }}
                                />
                                <textarea
                                  value={apprEdit.body}
                                  rows={14}
                                  onChange={(e) => setApprEdit({ ...apprEdit, body: e.target.value })}
                                  className="w-full text-sm rounded px-2.5 py-1.5 font-sans leading-relaxed"
                                  style={{ background: DCS.paper, border: `1px solid ${DCS.hairline}`, color: DCS.ink }}
                                />
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => saveAndApprove(row)}
                                    disabled={!!apprBusy || !apprEdit.subject.trim() || !apprEdit.body.trim()}
                                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-white disabled:opacity-40"
                                    style={{ background: DCS.gold }}
                                  >
                                    {apprBusy === row.lead_id
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <CheckCircle2 className="w-3 h-3" />}
                                    Save and approve
                                  </button>
                                  <button onClick={() => setApprEdit(null)}
                                          className="text-xs px-3 py-1.5 rounded-lg"
                                          style={{ background: DCS.cream, color: DCS.ink, border: `1px solid ${DCS.hairline}` }}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="rounded-lg p-3"
                                   style={{ background: DCS.paper, border: `1px solid ${DCS.hairline}` }}>
                                <p className="text-xs" style={{ color: DCS.ink, opacity: 0.55 }}>
                                  To: {copy.to || row.decision_maker?.email || '— no address yet'}
                                </p>
                                <p className="text-sm font-medium mb-2" style={{ color: DCS.ink }}>
                                  {copy.subject || 'No subject has been drafted'}
                                </p>
                                <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed"
                                     style={{ color: DCS.ink }}>
{copy.body || (full ? 'No email has been drafted for this lead yet.' : '')}
                                </pre>
                                {(copy.profiles || []).length > 0 && (
                                  <div className="mt-3 pt-2" style={{ borderTop: `1px solid ${DCS.hairline}` }}>
                                    <p className="text-xs mb-1.5" style={{ color: DCS.ink, opacity: 0.55 }}>
                                      Profiles that go with it
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {copy.profiles.map((p, i) => (
                                        <span key={i} className="text-xs px-2 py-0.5 rounded-full"
                                              style={{ background: DCS.cream, color: DCS.ink, border: `1px solid ${DCS.hairline}` }}>
                                          {p.ref || p.label || `Profile ${i + 1}`}
                                          {p.headline ? ` — ${p.headline}` : ''}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {copy.original_subject && (
                                  <p className="text-xs mt-2" style={{ color: DCS.gold }}>
                                    You edited this copy. The original is kept.
                                  </p>
                                )}
                              </div>
                            )}

                            {!editing && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  onClick={() => approveMail(row)}
                                  disabled={!!apprBusy || !row.can_approve || !copy.subject}
                                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-white disabled:opacity-40"
                                  style={{ background: DCS.gold }}
                                >
                                  {apprBusy === row.lead_id
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : <CheckCircle2 className="w-3 h-3" />}
                                  Approve and send
                                </button>
                                <button
                                  onClick={() => setApprEdit({
                                    leadId: row.lead_id,
                                    subject: copy.subject || '',
                                    body: copy.body || '',
                                  })}
                                  disabled={!!apprBusy || !full}
                                  className="text-xs px-3 py-1.5 rounded-lg disabled:opacity-40"
                                  style={{ background: DCS.cream, color: DCS.ink, border: `1px solid ${DCS.hairline}` }}
                                >
                                  Edit copy
                                </button>
                                <button
                                  onClick={() => rejectMail(row)}
                                  disabled={!!apprBusy}
                                  className="text-xs px-3 py-1.5 rounded-lg text-slate-500 hover:text-red-600 disabled:opacity-40"
                                >
                                  Reject
                                </button>
                                <span className="text-xs" style={{ color: DCS.ink, opacity: 0.5 }}>
                                  Approving opens the gate. It does not send this second.
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {visible.length === 0 && (
                    <p className="text-xs py-6 text-center" style={{ color: DCS.ink, opacity: 0.5 }}>
                      Nothing is waiting for you.
                    </p>
                  )}
                </div>

                {/* paging */}
                {approvals.count > 0 && (
                  <div className="flex items-center justify-between text-xs" style={{ color: DCS.ink }}>
                    <span style={{ opacity: 0.6 }}>
                      Showing {approvals.count === 0 ? 0 : apprOffset + 1}–
                      {apprOffset + (page.returned || 0)} of {approvals.count}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setApprOffset(Math.max(0, apprOffset - APPR_PAGE)); setApprSel({}); }}
                        disabled={apprOffset === 0}
                        className="px-2.5 py-1 rounded disabled:opacity-30"
                        style={{ background: DCS.cream, border: `1px solid ${DCS.hairline}` }}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => { setApprOffset(apprOffset + APPR_PAGE); setApprSel({}); }}
                        disabled={!page.has_more}
                        className="px-2.5 py-1 rounded disabled:opacity-30"
                        style={{ background: DCS.cream, border: `1px solid ${DCS.hairline}` }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {/* ---- the drip, directly beneath the section ---- */}
                <div className="rounded-lg p-4 space-y-3"
                     style={{ background: DCS.cream, border: `1px solid ${DCS.hairline}` }}
                     data-testid="bde-approvals-drip">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold"
                             style={{ color: DCS.ink }}>
                        <input
                          type="checkbox" checked={!!ds.enabled} disabled={dripBusy}
                          onChange={(e) => saveDrip(
                            { enabled: e.target.checked },
                            e.target.checked
                              ? 'Turn auto-approve ON?\n\nMails go out without you reading them '
                                + 'first. They will be released a few at a time, inside the window '
                                + 'and caps below, and every one is still recorded against your name.'
                              : null)}
                          data-testid="bde-drip-toggle"
                        />
                        <Mail className="w-4 h-4" style={{ color: DCS.gold }} />
                        Auto-approve and drip-send
                      </label>
                      <p className="text-xs mt-1 font-medium" style={{ color: DCS.gold }}>
                        Mails go out without you reading them first.
                      </p>
                    </div>
                    <button
                      onClick={stopDrip}
                      disabled={dripBusy}
                      className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-40 bg-red-600 hover:bg-red-500"
                      data-testid="bde-drip-stop"
                    >
                      <Power className="w-4 h-4" /> STOP the drip
                    </button>
                  </div>

                  {/* live numbers */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {[
                      ['Gone today', dl.sent_today ?? '—'],
                      ['Left in the daily cap', dl.cap_remaining ?? '—'],
                      ['In the last 5 minutes', `${dl.in_window_now ?? '—'} of ${ds.max_per_window ?? '—'}`],
                      ['Next one due', dl.next_due_at ? new Date(dl.next_due_at).toLocaleTimeString() : '—'],
                    ].map(([k, v]) => (
                      <div key={k} className="rounded p-2"
                           style={{ background: DCS.paper, border: `1px solid ${DCS.hairline}` }}>
                        <p style={{ color: DCS.ink, opacity: 0.55 }}>{k}</p>
                        <p className="text-sm font-semibold" style={{ color: DCS.ink }}>{v}</p>
                      </div>
                    ))}
                  </div>

                  {/* every limit, editable and visible */}
                  <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
                    <DripNum label="Most per window" value={ds.max_per_window} disabled={dripBusy}
                             onSave={(v) => saveDrip({ max_per_window: v })} />
                    <DripNum label="Window" value={ds.window_seconds} step={30} suffix="sec"
                             disabled={dripBusy} onSave={(v) => saveDrip({ window_seconds: v })} />
                    <DripNum label="Daily cap" value={ds.daily_cap} disabled={dripBusy}
                             onSave={(v) => saveDrip({ daily_cap: v })} />
                    <DripNum label="Gap" value={ds.interval_seconds} step={30} suffix="sec"
                             disabled={dripBusy} onSave={(v) => saveDrip({ interval_seconds: v })} />
                    <DripNum label="Jitter" value={ds.jitter_pct} step={0.05} suffix="of the gap"
                             disabled={dripBusy} onSave={(v) => saveDrip({ jitter_pct: v })} />
                    <DripNum label="From" value={ds.window_start_ist} step={0.5} suffix="IST"
                             disabled={dripBusy} onSave={(v) => saveDrip({ window_start_ist: v })} />
                    <DripNum label="Until" value={ds.window_end_ist} step={0.5} suffix="IST"
                             disabled={dripBusy} onSave={(v) => saveDrip({ window_end_ist: v })} />
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: DCS.ink }}>
                    <span style={{ opacity: 0.6 }}>Days</span>
                    {IST_DAYS.map((d, i) => {
                      const on = (ds.days || []).includes(i);
                      return (
                        <button key={d} disabled={dripBusy}
                                onClick={() => saveDrip({
                                  days: on ? (ds.days || []).filter((x) => x !== i)
                                           : [...(ds.days || []), i].sort(),
                                })}
                                className="px-2 py-0.5 rounded-full disabled:opacity-40"
                                style={on
                                  ? { background: DCS.gold, color: '#fff' }
                                  : { background: DCS.paper, color: DCS.ink, border: `1px solid ${DCS.hairline}` }}>
                          {d}
                        </button>
                      );
                    })}
                    <span className="ml-2" style={{ opacity: 0.6 }}>
                      {hhmm(ds.window_start_ist)}–{hhmm(ds.window_end_ist)} IST ·{' '}
                      {dl.window_open ? 'window open now' : 'window closed now'}
                    </span>
                  </div>

                  <p className="text-xs" style={{ color: DCS.ink, opacity: 0.6 }}>
                    The 3-in-5 ceiling and the daily cap are enforced at the moment of sending,
                    counted from what actually went out — so a restart, a second worker or a
                    hand-clicked send can never burst past them. Gaps are randomised by the
                    jitter above and spread across your configured mailboxes.
                    {dl.stopped_at && ` Stopped by ${dl.stopped_by || 'you'}.`}
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Top pipeline */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Top pipeline — biggest fees first</h2>
        <div className="overflow-x-auto max-md:hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase border-b">
                <th className="py-2 pr-4">Company / Role</th>
                <th className="py-2 pr-4">Decision maker</th>
                <th className="py-2 pr-4">Est. fee</th>
                <th className="py-2 pr-4">Stage</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.top_leads || []).map((l) => (
                <tr key={l.id}>
                  <td className="py-2 pr-4">
                    <p className="font-medium text-slate-800">{l.company?.name}</p>
                    <p className="text-xs text-slate-500">{l.signal?.job_title}</p>
                  </td>
                  <td className="py-2 pr-4">
                    <p className="text-slate-700">{l.contact?.name || '—'}</p>
                    <p className="text-xs text-slate-400">{l.contact?.title || ''}</p>
                  </td>
                  <td className="py-2 pr-4 font-semibold text-slate-800">{inr(l.signal?.estimated_fee)}</td>
                  <td className="py-2 pr-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STAGE_COLORS[l.stage] || ''}`}>
                      {STAGE_LABELS[l.stage] || l.stage}
                    </span>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => openLedger(l.id)}
                      disabled={ledgerBusy}
                      title="Where this lead came from, what it cost, and why it scores what it does"
                      className="text-xs px-2 py-1 mr-1 rounded bg-slate-50 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                    >
                      <Route className="w-3 h-3 inline -mt-0.5 mr-1" />Trace
                    </button>
                    {(l.contact && l.contact.email) ? (
                      <button
                        onClick={() => previewEngage(l.id)}
                        disabled={engageBusy}
                        title="Screen the database against this role, score the matches, and draft the email — nothing is sent until you confirm"
                        className="text-xs px-2 py-1 mr-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
                      >
                        <Send className="w-3 h-3 inline -mt-0.5 mr-1" />Send profiles
                      </button>
                    ) : (
                      <button
                        disabled
                        title="No decision-maker contact on this lead yet. Apollo sourcing runs in dry-run and never reveals a contact automatically — enrich this lead (or add a contact by hand) before profiles can be sent."
                        className="text-xs px-2 py-1 mr-1 rounded bg-slate-50 text-slate-400 cursor-not-allowed"
                      >
                        <Users className="w-3 h-3 inline -mt-0.5 mr-1" />Find contact
                      </button>
                    )}
                    {['replied', 'escalated', 'meeting'].includes(l.stage) && (
                      <button
                        onClick={() => convert(l.id)}
                        disabled={!!busy}
                        className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-40"
                      >
                        Won → Mandate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(!data?.top_leads || data.top_leads.length === 0) && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-400 text-sm">
                  No active leads yet — the hourly agent fills this automatically.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Phones: the same leads as stacked cards. */}
        <div className="md:hidden divide-y divide-slate-100">
          {(data?.top_leads || []).map((l) => (
            <div key={l.id} className="py-3 space-y-2" data-testid={`bde-lead-card-${l.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 break-words">{l.company?.name}</p>
                  <p className="text-xs text-slate-500 break-words">{l.signal?.job_title}</p>
                </div>
                <span className="text-sm font-semibold text-slate-800 shrink-0">{inr(l.signal?.estimated_fee)}</span>
              </div>
              <dl className="space-y-1 text-sm">
                <div className="flex gap-2">
                  <dt className="w-28 shrink-0 text-xs uppercase tracking-wide text-slate-400 pt-0.5">Decision maker</dt>
                  <dd className="min-w-0 flex-1 text-slate-700 truncate">
                    {l.contact?.name || '—'}{l.contact?.title ? ` · ${l.contact.title}` : ''}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 shrink-0 text-xs uppercase tracking-wide text-slate-400 pt-0.5">Stage</dt>
                  <dd className="min-w-0 flex-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STAGE_COLORS[l.stage] || ''}`}>
                      {STAGE_LABELS[l.stage] || l.stage}
                    </span>
                  </dd>
                </div>
              </dl>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => openLedger(l.id)}
                  disabled={ledgerBusy}
                  className="min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-50 text-xs text-slate-600 disabled:opacity-40"
                >
                  <Route className="w-3.5 h-3.5" /> Trace
                </button>
                <button
                  onClick={() => previewEngage(l.id)}
                  disabled={engageBusy}
                  className="min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-50 text-xs text-indigo-700 disabled:opacity-40"
                >
                  <Send className="w-3.5 h-3.5" /> Send profiles
                </button>
                {['replied', 'escalated', 'meeting'].includes(l.stage) && (
                  <button
                    onClick={() => convert(l.id)}
                    disabled={!!busy}
                    className="col-span-2 min-h-[44px] inline-flex items-center justify-center rounded-lg bg-green-50 text-xs text-green-700 disabled:opacity-40"
                  >
                    Won → Mandate
                  </button>
                )}
              </div>
            </div>
          ))}
          {(!data?.top_leads || data.top_leads.length === 0) && (
            <p className="py-6 text-center text-slate-400 text-sm">
              No active leads yet — the hourly agent fills this automatically.
            </p>
          )}
        </div>
      </div>

      {/* Where leads come from - the question the ledger exists to answer */}
      {sources && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between max-md:flex-col max-md:items-start max-md:gap-1">
            <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Coins className="w-4 h-4 text-amber-500 max-md:shrink-0" />
              Where leads come from — cost and return by channel
            </h2>
            <span className="text-xs text-slate-400">
              ₹{sources.rates?.inr_per_credit?.toFixed(2)} per Apollo credit
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total spend" value={inr(sources.totals?.spend_inr)} />
            <Stat label="Cost / reply" value={inr(sources.totals?.cost_per_reply_inr)} accent="text-emerald-700" />
            <Stat label="Cost / mandate" value={inr(sources.totals?.cost_per_mandate_inr)} accent="text-green-700" />
            <Stat label="Replies / mandates" value={`${sources.totals?.replies || 0} / ${sources.totals?.mandates || 0}`} />
          </div>
          <div className="overflow-x-auto max-md:hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase border-b">
                  <th className="py-2 pr-4">Source / segment</th>
                  <th className="py-2 pr-4">Leads</th>
                  <th className="py-2 pr-4">Contacted</th>
                  <th className="py-2 pr-4">Replies</th>
                  <th className="py-2 pr-4">Mandates</th>
                  <th className="py-2 pr-4">Spend</th>
                  <th className="py-2 pr-4">₹/lead</th>
                  <th className="py-2">₹/reply</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(sources.rows || []).map((r) => (
                  <tr key={`${r.source}/${r.segment}`}>
                    <td className="py-2 pr-4">
                      <p className="font-medium text-slate-800">{r.source}</p>
                      <p className="text-xs text-slate-500">{r.segment}</p>
                    </td>
                    <td className="py-2 pr-4">{r.leads}</td>
                    <td className="py-2 pr-4">{r.contacted}</td>
                    <td className="py-2 pr-4 text-emerald-700 font-medium">
                      {r.replies}{r.reply_rate_pct != null && (
                        <span className="text-xs text-slate-400"> ({r.reply_rate_pct}%)</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-green-700 font-semibold">{r.mandates}</td>
                    <td className="py-2 pr-4">{inr(r.cost_inr)}</td>
                    <td className="py-2 pr-4">{r.cost_per_lead_inr != null ? `₹${r.cost_per_lead_inr}` : '—'}</td>
                    <td className="py-2">{r.cost_per_reply_inr != null ? `₹${r.cost_per_reply_inr}` : '—'}</td>
                  </tr>
                ))}
                {(!sources.rows || sources.rows.length === 0) && (
                  <tr><td colSpan={8} className="py-6 text-center text-slate-400 text-sm">
                    No tracked sources yet — run the ledger backfill.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Phones: the same channel rows as stacked cards. */}
          <div className="md:hidden divide-y divide-slate-100">
            {(sources.rows || []).map((r) => (
              <div key={`m-${r.source}/${r.segment}`} className="py-3 space-y-1.5">
                <div>
                  <p className="font-semibold text-slate-800 break-words">{r.source}</p>
                  <p className="text-xs text-slate-500 break-words">{r.segment}</p>
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                  <div className="flex gap-2"><dt className="text-xs uppercase tracking-wide text-slate-400 pt-0.5">Leads</dt><dd className="text-slate-700">{r.leads}</dd></div>
                  <div className="flex gap-2"><dt className="text-xs uppercase tracking-wide text-slate-400 pt-0.5">Contacted</dt><dd className="text-slate-700">{r.contacted}</dd></div>
                  <div className="flex gap-2"><dt className="text-xs uppercase tracking-wide text-slate-400 pt-0.5">Replies</dt><dd className="text-emerald-700 font-medium">{r.replies}{r.reply_rate_pct != null ? ` (${r.reply_rate_pct}%)` : ''}</dd></div>
                  <div className="flex gap-2"><dt className="text-xs uppercase tracking-wide text-slate-400 pt-0.5">Mandates</dt><dd className="text-green-700 font-semibold">{r.mandates}</dd></div>
                  <div className="flex gap-2"><dt className="text-xs uppercase tracking-wide text-slate-400 pt-0.5">Spend</dt><dd className="text-slate-700">{inr(r.cost_inr)}</dd></div>
                  <div className="flex gap-2"><dt className="text-xs uppercase tracking-wide text-slate-400 pt-0.5">&#8377;/lead</dt><dd className="text-slate-700">{r.cost_per_lead_inr != null ? `₹${r.cost_per_lead_inr}` : '—'}</dd></div>
                  <div className="flex gap-2"><dt className="text-xs uppercase tracking-wide text-slate-400 pt-0.5">&#8377;/reply</dt><dd className="text-slate-700">{r.cost_per_reply_inr != null ? `₹${r.cost_per_reply_inr}` : '—'}</dd></div>
                </dl>
              </div>
            ))}
            {(!sources.rows || sources.rows.length === 0) && (
              <p className="py-6 text-center text-slate-400 text-sm">
                No tracked sources yet — run the ledger backfill.
              </p>
            )}
          </div>
          {sources.unattributed_cost_events > 0 && (
            <p className="text-xs text-amber-600">
              {sources.unattributed_cost_events} cost event(s) not yet attributed to a lead.
            </p>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="text-sm font-semibold text-slate-800">Engine policy</h2>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!settings?.auto_send} disabled={busy === 'auto_send'}
                   onChange={() => toggle('auto_send')} data-testid="bde-auto-send" />
            <Mail className="w-4 h-4 text-slate-400" />
            Auto-send outreach & follow-ups
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!settings?.auto_reply} disabled={busy === 'auto_reply'}
                   onChange={() => toggle('auto_reply')} data-testid="bde-auto-reply" />
            <MessageSquare className="w-4 h-4 text-slate-400" />
            AI answers info questions (meetings & pricing always come to you)
          </label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="text-slate-600">First-touches/day: <b>{settings?.daily_send_cap}</b></div>
          <div className="text-slate-600">Total sends/day: <b>{settings?.daily_total_send_cap}</b></div>
          <div className="text-slate-600">Apollo credits/day: <b>{settings?.apollo_daily_credit_cap}</b></div>
          <div className="text-slate-600">Send window: <b>09:30–19:00 IST, Mon–Sat</b></div>
        </div>
        <div className="pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 mb-1">Agent service key (for the hourly Daruwalla BDE agent only — treat like a password)</p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 select-all break-all">
              {showKey ? settings?.service_key : '••••••••••••••••••••••••'}
            </code>
            <button onClick={() => setShowKey(!showKey)} className="p-1 text-slate-400 hover:text-slate-600">
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              onClick={() => navigator.clipboard?.writeText(settings?.service_key || '')}
              className="p-1 text-slate-400 hover:text-slate-600"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Per-lead trace: origin, cost, timing, and why it scores what it does */}
      {ledger && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-2 sm:p-4 overflow-y-auto"
             onClick={() => setLedger(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full mt-4 sm:mt-10 p-4 sm:p-6 space-y-4"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Route className="w-4 h-4 text-slate-500" />
                Lead trace
              </h3>
              <button onClick={() => setLedger(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {ledger.loading && <p className="text-sm text-slate-500">Loading…</p>}
            {ledger.error && <p className="text-sm text-red-600">{ledger.error}</p>}

            {ledger.found && (
              <div className="space-y-4 text-sm">
                <div>
                  <p className="font-medium text-slate-900">{ledger.company?.name}</p>
                  <p className="text-slate-600">
                    {ledger.contact?.name} — {ledger.contact?.title}
                  </p>
                  <p className="text-xs text-slate-400">{ledger.contact?.email}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase">Sourced from</p>
                    <p className="font-medium text-slate-800">{ledger.provenance?.source_system || '—'}</p>
                    <p className="text-xs text-slate-500">{ledger.provenance?.source_detail}</p>
                    <p className="text-xs text-slate-400 mt-1">{ledger.provenance?.sourced_at_ist}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase">Procurement cost</p>
                    <p className="font-medium text-slate-800">
                      ₹{(ledger.costs?.total_inr || 0).toFixed(2)}
                      <span className="text-xs text-slate-400"> / ${(ledger.costs?.total_usd || 0).toFixed(4)}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {(ledger.costs?.credits_total || 0).toFixed(2)} Apollo credits
                    </p>
                  </div>
                </div>

                {ledger.scoring && (
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                    <p className="text-xs text-emerald-800 uppercase flex items-center gap-1">
                      <Target className="w-3 h-3" /> Expected value
                    </p>
                    <p className="text-lg font-bold text-emerald-900">
                      {inr(ledger.scoring.expected_value_inr)}
                      <span className="text-xs font-normal text-emerald-700">
                        {'  '}= {inr(ledger.scoring.estimated_fee_inr)} × {(ledger.scoring.win_probability * 100).toFixed(2)}%
                      </span>
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.entries(ledger.scoring.breakdown || {}).map(([k, v]) => (
                        <span key={k}
                              className={`text-xs px-1.5 py-0.5 rounded ${v >= 1 ? 'bg-white text-emerald-800' : 'bg-white text-red-600'}`}>
                          {k} ×{v}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-emerald-700 mt-2">
                      {ledger.scoring.cohort_observations > 0
                        ? `Blended with ${ledger.scoring.cohort_observations} observation(s) from this cohort.`
                        : 'Rules prior — not enough outcome data yet to learn from.'}
                      {ledger.scoring.roi_ratio ? ` Return on cost: ${ledger.scoring.roi_ratio}×.` : ''}
                    </p>
                  </div>
                )}

                {ledger.funnel && (
                  <div className="text-xs text-slate-600 space-y-0.5">
                    <p className="text-xs text-slate-500 uppercase mb-1">Timing</p>
                    {ledger.funnel.hours_to_first_touch != null &&
                      <p>Sourced → first email: <b>{ledger.funnel.hours_to_first_touch} h</b></p>}
                    {ledger.funnel.hours_to_reply != null &&
                      <p>First email → reply: <b>{ledger.funnel.hours_to_reply} h</b> after {ledger.funnel.touches_to_reply} touch(es)</p>}
                    {ledger.funnel.outcome && <p>Outcome: <b>{ledger.funnel.outcome}</b></p>}
                    {ledger.funnel.touches != null && <p>Emails sent: <b>{ledger.funnel.touches}</b></p>}
                  </div>
                )}

                {ledger.search_spec && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-600">
                      The exact search that found this lead (re-runnable)
                    </summary>
                    <pre className="mt-2 bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto text-[11px]">
{JSON.stringify(ledger.search_spec.filters, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Screen → score → send. Nothing leaves until the second click. */}
      {engage && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-2 sm:p-4 overflow-y-auto"
             onClick={() => setEngage(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full mt-4 sm:mt-10 p-4 sm:p-6 space-y-4"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Send className="w-4 h-4 text-slate-500" />
                Send profiles
              </h3>
              <button onClick={() => setEngage(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {engage.loading && (
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Screening the database against this role…
              </p>
            )}
            {engage.error && <p className="text-sm text-red-600">{engage.error}</p>}

            {engage.verdict && (
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    engage.verdict.action === 'send' ? 'bg-green-100 text-green-800'
                    : engage.verdict.action === 'review' ? 'bg-amber-100 text-amber-800'
                    : 'bg-slate-100 text-slate-600'}`}>
                    {engage.verdict.action === 'send' ? 'Ready to send'
                      : engage.verdict.action === 'review' ? 'Held for review'
                      : engage.verdict.action === 'capability' ? 'No profiles — capability email'
                      : 'Cannot send'}
                  </span>
                  <p className="text-slate-600 flex-1">{engage.verdict.reason}</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1">
                  {engage.verdict?.family_unreadable ? (
                    <>
                      <p className="text-amber-700 font-medium flex items-start gap-1">
                        <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        I could not read "{engage.role || 'this title'}" into a discipline —
                        nothing was screened against the candidate pool.
                      </p>
                      <div className="flex items-center gap-2 pt-1">
                        <select
                          value={manualFamily}
                          onChange={(e) => setManualFamily(e.target.value)}
                          className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
                        >
                          <option value="">Pick a discipline by hand…</option>
                          {(engage.verdict.available_families || []).map((f) => (
                            <option key={f.family} value={f.family}>{f.label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => manualFamily && previewEngage(engage.leadId, manualFamily)}
                          disabled={!manualFamily || engageBusy}
                          className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
                        >
                          Re-screen with this discipline
                        </button>
                      </div>
                    </>
                  ) : (
                    <p><b className="text-slate-800">{engage.role || '—'}</b> read as{' '}
                      <b className="text-slate-800">{engage.discipline || 'unclear'}</b>
                      {engage.discipline_confidence != null &&
                        ` (confidence ${engage.discipline_confidence})`}
                      {engage.seniority ? `, ${engage.seniority} level` : ''}</p>
                  )}
                  <p>
                    <Users className="w-3 h-3 inline -mt-0.5 mr-1" />
                    {engage.in_discipline} of {engage.pool_size} candidates are in that discipline
                  </p>
                  {engage.suppressed && (engage.suppressed.already_sent > 0 || engage.suppressed.own_employer > 0) && (
                    <p className="text-slate-500">
                      <ShieldCheck className="w-3 h-3 inline -mt-0.5 mr-1" />
                      Held back: {engage.suppressed.already_sent} already sent to this company,{' '}
                      {engage.suppressed.own_employer} work there
                    </p>
                  )}
                </div>

                {(engage.shortlist || []).length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Would send</p>
                    <div className="space-y-1">
                      {engage.shortlist.map((c) => (
                        <div key={c.candidate_id}
                             className="flex items-center justify-between bg-white border border-slate-200 rounded px-3 py-2">
                          <div>
                            <p className="font-medium text-slate-800">{c.name}</p>
                            <p className="text-xs text-slate-500">
                              {c.designation}{c.years ? ` · ${c.years} yrs` : ''}
                              {c.location ? ` · ${String(c.location).split(',')[0]}` : ''}
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            c.score >= 70 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                            {c.score}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {engage.email && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">The email</p>
                    <div className="bg-slate-50 rounded-lg p-3 text-xs">
                      <p className="text-slate-500">To: {engage.email.to || '—'}</p>
                      <p className="font-medium text-slate-800 mb-2">{engage.email.subject}</p>
                      <pre className="whitespace-pre-wrap font-sans text-slate-700 leading-relaxed">
{engage.email.body}
                      </pre>
                    </div>
                  </div>
                )}

                {engage.done ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs space-y-1">
                    <p className="text-green-800 font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" />
                      {engage.email?.sent ? 'Sent' : 'Run finished'}
                    </p>
                    {engage.email?.error && <p className="text-red-600">{engage.email.error}</p>}
                    {engage.shortlist_url && (
                      <p className="text-slate-600 break-all">Shortlist: {engage.shortlist_url}</p>
                    )}
                    {engage.followup?.calendar?.link && (
                      <p><a href={engage.followup.calendar.link} target="_blank" rel="noreferrer"
                            className="text-indigo-600 underline">Follow-up in your calendar</a></p>
                    )}
                    {engage.followup?.calendar?.error && (
                      <p className="text-amber-700">Calendar: {engage.followup.calendar.error}</p>
                    )}
                  </div>
                ) : engage.verdict.action === 'send' ? (
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => confirmEngage(engage.leadId)}
                      disabled={engageBusy || engage.sending}
                      className="text-sm px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
                    >
                      {engage.sending ? 'Sending…' : 'Send it'}
                    </button>
                    <button onClick={() => setEngage(null)}
                            className="text-sm px-4 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">
                      Not yet
                    </button>
                    <span className="text-xs text-slate-400">Nothing has been sent or created yet.</span>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">
                    Nothing was sent. Nothing was created.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
